// Shared types for the generative-transform layer.
//
// A Transform is a pure function from a Song (+ a target voice's
// const-name + an RNG seed) to either an Immer recipe that will mutate
// the Song, or an error explaining why the transform doesn't apply
// here. The Command-palette wiring iterates over voices in the current
// Song and produces one runnable Command per (transform × applicable
// voice) — voices for which the transform errors are filtered out so
// the palette only shows commands the user can actually run.

import type { EditRecipe } from '../blocks/editContext';
import type { Song, VoiceBlock } from '../blocks/types';

export type TransformOk = {
  ok: true;
  recipe: EditRecipe;
  /** Short human-readable summary, used as the Command label. */
  summary: string;
};

export type TransformErr = { ok: false; reason: string };

export type TransformResult = TransformOk | TransformErr;

export type TransformContext = {
  song: Song;
  /** The voice's const-name (e.g. "bass"). Const-name is the stable
   *  identifier across re-parses; block ids are not. */
  voiceName: string;
  /** Caller-supplied seed for the PRNG. Pass a different seed each
   *  invocation to get a different result; reuse to reproduce. */
  seed: number;
};

export type Transform = {
  /** Stable id used as a prefix in Command ids. */
  id: string;
  /** Top-level grouping in the Command palette. */
  category: 'rhythm' | 'tonal' | 'density';
  /** Verb shown in the palette, e.g. "Humanize". The full label
   *  becomes "Humanize bass" at command-build time. */
  verb: string;
  /** Optional one-line description shown under the label. */
  description?: string;
  /** Apply the transform. Return `ok: false` to filter this voice out
   *  of the palette (e.g. tonal transforms on a sample voice). */
  apply: (ctx: TransformContext) => TransformResult;
};

// Return all named voice blocks in the song, in source order. The
// palette generates commands keyed off this list. A "voice block" is
// any const whose value is a voice.s or voice.note expression.
export function namedVoices(song: Song): { name: string; voice: VoiceBlock }[] {
  const out: { name: string; voice: VoiceBlock }[] = [];
  for (const stmt of song.statements) {
    if (stmt.kind !== 'const') continue;
    const expr = stmt.expr;
    if (expr.kind === 'voice.s' || expr.kind === 'voice.note') {
      out.push({ name: stmt.name, voice: expr });
    }
  }
  return out;
}
